import React, {useState} from 'react';

import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';

import {DeleteIcon, Input} from '@edume/magnificent';

import FormWrapper from '../../../containers/FormWrapper';
import {StyledModal} from '../../../containers/StyledModals';
import addFormState from '../../../hocs/addFormState';
import useValidation from '../../../hooks/useValidation';
import {
  isGroupNameNotEmpty,
  isGroupNameNotTooLong,
} from '../../../utils/validation';

const connector = connect((state) => ({
  editTeamInProgress: state.team.get('editTeamInProgress'),
}));

// eslint-disable-next-line complexity
const EditTeamOrGroup = ({
  originalName,
  onSave,
  onCancel,
  editType,
  isGroup,
  editTeamInProgress,
  formError,
  resetFormError,
  onGroupDelete,
  intl,
}) => {
  const [newName, setName] = useState(originalName);

  const [nameNotEmpty, isNameNotEmptyDisplay, unsetForceValidDisplay] =
    useValidation(newName, isGroupNameNotEmpty);

  const [nameNotTooLong, isNameNotTooLongDisplay] = useValidation(
    newName,
    isGroupNameNotTooLong
  );

  const isNameValid = nameNotEmpty && nameNotTooLong;

  const updateName = (name) => {
    if (formError) {
      resetFormError();
    }
    setName(name);
  };

  const getIntlKeys = () => {
    const keys = {
      add: {
        group: {
          titleKey: 'Groups.createGroupModalTitle',
          formLabelKey: 'Groups.groupNameLabel',
          primaryButtonKey: 'Groups.createGroupConfirm',
        },
        team: {
          titleKey: 'Teams.createTeam',
          formLabelKey: 'Teams.teamNameLabel',
          primaryButtonKey: 'Teams.createTeamConfirm',
        },
      },
      edit: {
        group: {
          titleKey: 'Groups.editGroupModalTitle',
          formLabelKey: 'Groups.groupNameLabel',
          primaryButtonKey: 'Groups.editGroupConfirm',
        },
        team: {
          titleKey: 'Teams.editTeamName',
          formLabelKey: 'Teams.teamNameLabel',
          primaryButtonKey: 'Teams.editTeamConfirm',
        },
      },
    };

    const type = isGroup ? 'group' : 'team';
    return keys[editType][type];
  };

  const onSubmit = () => {
    if (formError) {
      resetFormError();
    }
    unsetForceValidDisplay();
    if (isNameValid) {
      onSave(newName);
    }
  };

  const {titleKey, formLabelKey, primaryButtonKey} = getIntlKeys();

  const nameErrors = [
    'child_group_name_already_exists',
    'group_name_already_exists',
  ];
  const userFormError = formError && nameErrors.includes(formError);

  const isInvalid =
    userFormError || !isNameNotEmptyDisplay || !isNameNotTooLongDisplay;

  const primaryButtonProps = {
    type: 'primary',
    onClick: () => onSubmit(newName),
    disabled: editTeamInProgress,
    dataAutomation: 'confirm-create-team-or-group-button',
  };

  const secondaryButtonProps = {
    type: 'secondary',
    onClick: onCancel,
    dataAutomation: 'cancel-create-team-or-group-button',
  };

  let errorMessageId;

  if (userFormError) {
    errorMessageId = `Error.${formError}`;
  } else if (!nameNotTooLong) {
    errorMessageId = isGroup
      ? 'Groups.nameTooLongError'
      : 'Teams.nameTooLongError';
  } else if (!nameNotEmpty) {
    errorMessageId = isGroup
      ? 'Groups.emptyGroupError'
      : 'Teams.emptyTeamError';
  }

  const errorText =
    (errorMessageId && intl.formatMessage({id: errorMessageId})) || ' ';

  const body = () => (
    <Input
      type='text'
      label={intl.formatMessage({id: formLabelKey})}
      value={newName}
      onChangeValue={updateName}
      errorText={errorText}
      inputKey='editTeamName'
      onEnterKey={() => onSubmit(newName)}
      autoFocus
      isInvalid={isInvalid}
      includeBottomMargin={false}
      dataAutomation='team-or-group-name-input'
    />
  );

  const content = {
    title: intl.formatMessage({
      id: titleKey,
    }),
    body: body(),
    buttons: [
      {
        ...secondaryButtonProps,
        text: intl.formatMessage({id: 'Button.cancel'}),
      },
      {
        ...primaryButtonProps,
        text: intl.formatMessage({id: primaryButtonKey}),
      },
    ],
  };

  if (isGroup && onGroupDelete) {
    content.bottomLeftContent = {
      type: 'link',
      text: intl.formatMessage({id: 'Groups.deleteGroup'}),
      dataAutomation: 'delete-group-button',
      onClick: () => {
        onGroupDelete();
      },
      icon: <DeleteIcon colour='errorPrimary' />,
      linkColour: 'red',
      linkSize: 'snail',
    };
  }

  return (
    <FormWrapper onClose={onCancel}>
      <StyledModal onClose={onCancel} content={content} />
    </FormWrapper>
  );
};

EditTeamOrGroup.propTypes = {
  //own props
  originalName: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  editType: PropTypes.oneOf(['add', 'edit']).isRequired,
  isGroup: PropTypes.bool,
  onGroupDelete: PropTypes.func,

  //from wrappers
  editTeamInProgress: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  formError: PropTypes.string,
  resetFormError: PropTypes.func.isRequired,
};

EditTeamOrGroup.defaultProps = {
  originalName: '',
};

export default injectIntl(addFormState(connector(EditTeamOrGroup)));
